import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingsService } from '@services/settings.service'
import { SettingUpdate, SettingCreate } from '@/types'

/**
 * Get all public settings (no auth required)
 */
export function usePublicSettings() {
  return useQuery({
    queryKey: ['settings', 'public'],
    queryFn: () => settingsService.getPublicSettings(),
  })
}

/**
 * Get all settings (admin only)
 */
export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsService.getAllSettings(),
  })
}

/**
 * Get a specific setting by key (admin only)
 */
export function useSetting(key: string) {
  return useQuery({
    queryKey: ['setting', key],
    queryFn: () => settingsService.getSettingByKey(key),
    enabled: !!key,
  })
}

/**
 * Get settings by category (admin only)
 */
export function useSettingsByCategory(category: string) {
  return useQuery({
    queryKey: ['settings', 'category', category],
    queryFn: () => settingsService.getSettingsByCategory(category),
    enabled: !!category,
  })
}

/**
 * Update a single setting (admin only)
 */
export function useUpdateSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      settingsService.updateSetting(key, value),
    onSuccess: (data) => {
      // Invalidate all settings queries to refetch latest data
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      queryClient.invalidateQueries({ queryKey: ['setting', data.key] })
      if (data.isPublic) {
        queryClient.invalidateQueries({ queryKey: ['settings', 'public'] })
      }
    },
  })
}

/**
 * Batch update multiple settings (admin only)
 */
export function useUpdateSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updates: SettingUpdate[]) => settingsService.updateSettings(updates),
    onSuccess: () => {
      // Invalidate all settings queries
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}

/**
 * Create a new setting (admin only)
 */
export function useCreateSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: SettingCreate) => settingsService.createSetting(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}

/**
 * Delete a setting (admin only)
 */
export function useDeleteSetting() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (key: string) => settingsService.deleteSetting(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
    },
  })
}
